import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import {
  createSalesPayment,
  fetchPaymentById,
  updatePayment
} from '../../../services/salesPayments';
import { fetchSubscriptions } from '../../../services/salesSubscriptionService';
import { fetchTaxes } from '../../../services/taxService';
import { fetchDiscounts } from '../../../services/discountService';

const AddEditPayment = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Payment ID for editing
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const querySubscriptionId = searchParams.get('subscription_id'); // Get subscription_id from URL

  // Form State
  const [formData, setFormData] = useState({
    subscription_id: querySubscriptionId || '', // Auto-fill from URL if exists
    amount_paid: '',
    payment_date: new Date().toISOString().split('T')[0], // Default: Today
    transaction_id: '',
    tax_id: '',
    discount_id: ''
  });

  // Dropdown Data
  const [subscriptions, setSubscriptions] = useState<
    { subscription_id: string; plan: { plan_name: string; pricing: number } }[]
  >([]);
  const [taxes, setTaxes] = useState<{ tax_id: string; tax_rate: number }[]>(
    []
  );
  const [discounts, setDiscounts] = useState<
    { discount_id: string; discount_value: number }[]
  >([]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch dropdown options
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [subscriptionsData, taxesData, discountsData] = await Promise.all(
          [fetchSubscriptions(), fetchTaxes(), fetchDiscounts()]
        );
        setSubscriptions(subscriptionsData);
        setTaxes(taxesData);
        setDiscounts(discountsData);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadDropdowns();
  }, []);

  // Fetch existing payment if editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      const loadPayment = async () => {
        try {
          const payment = await fetchPaymentById(id);
          setFormData({
            subscription_id: payment.subscription.subscription_id.toString(),
            amount_paid: payment.amount_paid.toString(),
            payment_date: payment.payment_date.split('T')[0],
            transaction_id: payment.transaction_id,
            tax_id: payment.tax_id ? payment.tax_id.toString() : '',
            discount_id: payment.discount_id
              ? payment.discount_id.toString()
              : ''
          });
        } catch (error) {
          setError('Failed to load payment details. Please try again.');
          console.error('Error fetching payment:', error);
        } finally {
          setLoading(false);
        }
      };
      loadPayment();
    }
  }, [id]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLSelectElement;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (id) {
        await updatePayment(id, formData);
        alert('Payment updated successfully');
      } else {
        await createSalesPayment(formData);
        alert('Payment added successfully');
      }
      navigate('/sales/payments/all-payments');
    } catch (error) {
      setError('Error submitting payment. Please try again.');
      console.error('Error submitting payment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>{id ? 'Edit' : 'Add'} Payment</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading payment details...</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {/* Subscription Selection (Only if subscription_id is NOT in query params) */}
          {!querySubscriptionId && (
            <Form.Group>
              <Form.Label>Subscription</Form.Label>
              <Form.Select
                name="subscription_id"
                value={formData.subscription_id}
                onChange={handleChange}
                required
                disabled={submitting}
              >
                <option value="">Select Subscription</option>
                {subscriptions.map(sub => (
                  <option key={sub.subscription_id} value={sub.subscription_id}>
                    {sub.subscription_id}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* Amount Paid */}
          <Form.Group>
            <Form.Label>Amount Paid</Form.Label>
            <Form.Control
              type="number"
              name="amount_paid"
              value={formData.amount_paid}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Payment Date */}
          <Form.Group>
            <Form.Label>Payment Date</Form.Label>
            <Form.Control
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Transaction ID */}
          <Form.Group>
            <Form.Label>Transaction ID</Form.Label>
            <Form.Control
              type="text"
              name="transaction_id"
              value={formData.transaction_id}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Tax Selection */}
          <Form.Group>
            <Form.Label>Tax</Form.Label>
            <Form.Select
              name="tax_id"
              value={formData.tax_id}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="">Select Tax</option>
              {taxes.map(tax => (
                <option key={tax.tax_id} value={tax.tax_id}>
                  {tax.tax_rate}%
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Discount Selection */}
          <Form.Group>
            <Form.Label>Discount</Form.Label>
            <Form.Select
              name="discount_id"
              value={formData.discount_id}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="">Select Discount</option>
              {discounts.map(discount => (
                <option key={discount.discount_id} value={discount.discount_id}>
                  ₹{discount.discount_value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Submit Button */}
          <Button type="submit" className="mt-3" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              `${id ? 'Update' : 'Add'} Payment`
            )}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default AddEditPayment;
